import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, Coins, Percent, Loader2, Inbox } from "lucide-react";

export const PromoRedemptionsDialog = ({ code, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    api.get(`/admin/promo-codes/${code.id}/redemptions`)
      .then(({ data }) => setRows(data.redemptions))
      .finally(() => setLoading(false));
  }, [code]);

  const totalCredits = rows.reduce((s, r) => s + (r.credits || 0), 0);

  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="popover-solid border-white/10 max-w-2xl" data-testid="promo-redemptions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5 text-aurora-purple" /> Utilisations — <span className="font-mono text-aurora-cyan">{code?.code}</span>
          </DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-zinc-500">
            {code?.campaign || "Sans campagne"}{code?.label ? ` · ${code.label}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 mb-2">
          <div className="flex items-center gap-1.5 font-mono text-xs text-zinc-300">
            <Users className="w-4 h-4 text-aurora-purple" /> {rows.length} utilisation{rows.length > 1 ? "s" : ""}
          </div>
          {code?.reward_type === "credits" ? (
            <div className="flex items-center gap-1.5 font-mono text-xs text-aurora-cyan">
              <Coins className="w-4 h-4" /> {totalCredits} crédits offerts
            </div>
          ) : (
            <div className="flex items-center gap-1.5 font-mono text-xs text-aurora-cyan">
              <Percent className="w-4 h-4" /> -{code?.discount_pct}% par utilisateur
            </div>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto rounded-sm border border-white/10">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center" data-testid="redemptions-empty">
              <Inbox className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="font-mono text-xs text-zinc-500">Personne n'a encore utilisé ce code.</p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="px-4 py-2.5">Client</th>
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5 text-right">Gain</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`redemption-${r.id}`} className="border-b border-white/5 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="text-sm text-white truncate">{r.name || "—"}</div>
                      <div className="font-mono text-[10px] text-zinc-500 truncate">{r.email}</div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-zinc-400">
                      {(r.redeemed_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-sm text-aurora-cyan">
                      {r.reward_type === "discount" ? `-${r.discount_pct}%` : `+${r.credits}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
